import { useMemo, useRef, useState } from "react";
import { formatSEK0, formatSEKCompact } from "../lib/format";

export interface TrendPoint {
  date: string; // YYYY-MM-DD
  value: number;
}

interface TrendChartProps {
  data: TrendPoint[];
  /** CSS color for the line — defaults to the brand primary */
  color?: string;
  height?: number;
  /** Compact mode: no axis labels, tighter — for small cards */
  compact?: boolean;
}

const W = 600;

function formatShortDate(date: string): string {
  const d = new Date(date);
  return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
}

/**
 * A single-series area trend (net worth / portfolio value over time).
 * One series — the card title names it, so there is no legend; hover shows a
 * crosshair + tooltip; the latest value is direct-labeled by the parent.
 */
export function TrendChart({ data, color = "var(--primary)", height = 180, compact = false }: TrendChartProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const points = useMemo(
    () => [...data].sort((a, b) => a.date.localeCompare(b.date)),
    [data]
  );

  if (points.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        Historik byggs upp allteftersom — kom tillbaka imorgon
      </div>
    );
  }

  const padX = compact ? 4 : 8;
  const padTop = 12;
  const padBottom = compact ? 8 : 24;
  const H = height;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || Math.max(Math.abs(max), 1) * 0.1;
  const yMin = min - span * 0.12;
  const yMax = max + span * 0.12;

  const x = (i: number) =>
    padX + (i / (points.length - 1)) * (W - padX * 2);
  const y = (v: number) =>
    padTop + (1 - (v - yMin) / (yMax - yMin)) * (H - padTop - padBottom);

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`)
    .join(" ");
  const areaPath = `${linePath} L ${x(points.length - 1).toFixed(1)} ${H - padBottom} L ${x(0).toFixed(1)} ${H - padBottom} Z`;

  const gridValues = compact
    ? []
    : [yMin + (yMax - yMin) * 0.25, yMin + (yMax - yMin) * 0.75];

  function handleMove(event: React.PointerEvent) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rel = ((event.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((rel - padX) / (W - padX * 2)) * (points.length - 1));
    setHoverIndex(Math.max(0, Math.min(points.length - 1, idx)));
  }

  const hover = hoverIndex !== null ? points[hoverIndex] : null;
  const first = points[0]!;

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full touch-none"
        style={{ height: "auto" }}
        onPointerMove={handleMove}
        onPointerLeave={() => setHoverIndex(null)}
        role="img"
        aria-label={`Trend från ${formatShortDate(first.date)}: ${formatSEK0(points[points.length - 1]!.value)}`}
      >
        {gridValues.map((v, i) => (
          <g key={i}>
            <line
              x1={padX}
              x2={W - padX}
              y1={y(v)}
              y2={y(v)}
              stroke="var(--border)"
              strokeWidth={1}
            />
            <text
              x={W - padX}
              y={y(v) - 4}
              textAnchor="end"
              className="fill-muted-foreground"
              fontSize={11}
            >
              {formatSEKCompact(v)}
            </text>
          </g>
        ))}

        <path d={areaPath} fill={color} opacity={0.08} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />

        {hoverIndex !== null && hover && (
          <g>
            <line
              x1={x(hoverIndex)}
              x2={x(hoverIndex)}
              y1={padTop}
              y2={H - padBottom}
              stroke="var(--muted-foreground)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <circle
              cx={x(hoverIndex)}
              cy={y(hover.value)}
              r={4.5}
              fill={color}
              stroke="var(--card)"
              strokeWidth={2}
            />
          </g>
        )}

        {!compact && (
          <>
            <text x={padX} y={H - 6} className="fill-muted-foreground" fontSize={11}>
              {formatShortDate(first.date)}
            </text>
            <text
              x={W - padX}
              y={H - 6}
              textAnchor="end"
              className="fill-muted-foreground"
              fontSize={11}
            >
              {formatShortDate(points[points.length - 1]!.date)}
            </text>
          </>
        )}
      </svg>

      {hover && (
        <div
          className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md"
          style={{
            left: `${((x(hoverIndex!) / W) * 100).toFixed(1)}%`,
          }}
        >
          <div className="text-muted-foreground">{formatShortDate(hover.date)}</div>
          <div className="font-semibold tabular-nums">{formatSEK0(hover.value)}</div>
        </div>
      )}
    </div>
  );
}
